import React, { useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import AnnotationsModule from "highcharts/modules/annotations";
import {
  Box,
  Button,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useStyles } from "../../../theme/appStyles";
import { IProductPriceSplineTableChart } from "../../../Api/Interface/api.interface";
import dayjs from "dayjs";
import Pagination from "../../../Components/Pagination";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Collapse } from "@mui/material";

AnnotationsModule(Highcharts);

interface IProductPriceSplineChartRecord {
  data: IProductPriceSplineTableChart[];
}

const ProductPriceSplineChart: React.FC<IProductPriceSplineChartRecord> = ({
  data,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [showChart, setShowChart] = useState(false);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  const parsedSeries: Highcharts.SeriesSplineOptions[] = (data || []).map(
    (series) => ({
      type: "spline",
      name: series.name,
      data: (series.data || []).map(([dateString, value]) => [
        new Date(dateString).getTime(),
        value,
      ]),
    })
  );

  const allTimestampsSet = new Set<string>();
  data.forEach((series) => {
    series.data.forEach(([dateString]) => {
      allTimestampsSet.add(dateString);
    });
  });

  const allTimestamps = Array.from(allTimestampsSet).sort();

  const tableRows = allTimestamps.map((timestamp) => {
    const row: Record<string, any> = { timestamp };
    data.forEach((series) => {
      const found = series.data.find(([date]) => date === timestamp);
      row[series.name] = found ? found[1] : "-";
    });
    return row;
  });

  const filteredRows = tableRows.filter((row) => {
    const current = dayjs(row.timestamp);

    if (startDate && current.isBefore(dayjs(startDate), "day")) return false;
    if (endDate && current.isAfter(dayjs(endDate), "day")) return false;

    return true;
  });

  const rowsToDisplay = filteredRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const options: Highcharts.Options = {
    chart: {
      type: "spline",
      style: {
        fontFamily: '"Play", sans-serif',
      },
    },
    title: {
      useHTML: true,
      text: `<div class="${classes.h4Quoto}">Product Price Trends by Store (Spline Chart)</div>`,
    },
    xAxis: {
      type: "datetime",
      title: {
        text: `<div class="${classes.h5}">Date</div>`,
      },
      labels: {
        format: "{value:%Y-%m-%d}",
      },
    },
    yAxis: {
      title: {
        text: `<div class="${classes.h5}">Price ($)</div>`,
      },
    },
    tooltip: {
      shared: true,
      xDateFormat: "%Y-%m-%d %H:%M:%S",
      // crosshairs: true,
    },
    plotOptions: {
      spline: {
        marker: {
          enabled: true,
        },
      },
    },
    legend: {
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
    },
    series: parsedSeries,
  };

  return (
    <Box>
      <Box>
        <TableContainer>
          {(() => {
            const records: Array<{
              price: number;
              supplier: string;
              timestamp: string;
              grade: string;
              priceListId: string;
            }> = [];

            // filteredRows.forEach((row) => {
            //   data.forEach((series) => {
            //     const value = row[series.name];
            //     if (value !== "-" && !isNaN(value)) {
            //       const foundGrade =
            //         data
            //           .flatMap((s) => s.data)
            //           .find(([date]) => date === row.timestamp)?.[2] || "-";

            //       records.push({
            //         price: Number(value),
            //         supplier: series.name,
            //         timestamp: row.timestamp,
            //         grade: foundGrade,
            //         priceListId: foundData[3] || "-",
            //       });
            //     }
            //   });
            // });
            filteredRows.forEach((row) => {
              data.forEach((series) => {
                const value = row[series.name];
                if (value !== "-" && !isNaN(value)) {
                  const foundData =
                    data
                      .flatMap((s) => s.data)
                      .find(([date]) => date === row.timestamp) || [];

                  records.push({
                    price: Number(value),
                    supplier: series.name,
                    timestamp: row.timestamp,
                    grade: foundData[2] || "-",
                    priceListId: foundData[3] || "-",
                  });
                }
              });
            });

            if (records.length === 0) return null;

            const minRecord = records.reduce((min, r) =>
              r.price < min.price ? r : min
            );

            return (
              <Paper
                elevation={6}
                sx={{
                  mb: 3,
                  borderRadius: 4,
                  overflow: "hidden",
                  border: "1px solid #dcedc8",
                  boxShadow: "none",
                  backgroundColor: theme.palette.cardAdd.bgColor,
                }}
              >
                <Box
                  sx={{
                    background: "#1b5e20",
                    color: "#fff",
                    textAlign: "center",
                    p: 1.4,
                    fontWeight: 700,
                    fontSize: "16px",
                    letterSpacing: 0.7,
                  }}
                >
                  ⬇ Lowest Price in Selected Range
                </Box>

                <Grid container alignItems="center" spacing={2} sx={{ p: 3 }}>
                  <Grid item xs={12} sm={4}>
                    <Grid container spacing={2} mb={4}>
                      <Grid item xs={12} sm={12} md={12}>
                        <Box
                          sx={{
                            p: 1,
                            // borderRadius: 2,
                            // backgroundColor: theme.palette.cardAdd.bgColor,
                            // boxShadow: "0px 3px 8px rgba(0,0,0,0.1)",
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          <Typography variant="subtitle2" color="textSecondary">
                            From Date
                          </Typography>
                          <TextField
                            type="date"
                            value={startDate || ""}
                            onChange={(e) => {
                              setStartDate(e.target.value);
                              setPage(0);
                            }}
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <CalendarTodayIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            sx={{
                              backgroundColor: "#f0f0f0",
                              borderRadius: 1,
                              "& .MuiInputBase-input": { padding: "10px" },
                            }}
                          />
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={12} md={12}>
                        <Box
                          sx={{
                            p: 1,
                            // borderRadius: 2,
                            // backgroundColor: "#ffffff",
                            // boxShadow: "0px 3px 8px rgba(0,0,0,0.1)",
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          <Typography variant="subtitle2" color="textSecondary">
                            To Date
                          </Typography>
                          <TextField
                            type="date"
                            value={endDate || ""}
                            onChange={(e) => {
                              setEndDate(e.target.value);
                              setPage(0);
                            }}
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <CalendarTodayIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            sx={{
                              backgroundColor: "#f0f0f0",
                              borderRadius: 1,
                              "& .MuiInputBase-input": { padding: "10px" },
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                  {/* Price Badge */}
                  <Grid item xs={12} sm={4} textAlign="center">
                    <Box
                      sx={{
                        display: "inline-block",
                        px: 3,
                        py: 1.5,
                        bgcolor: "#e8f5e9",
                        borderRadius: "50px",
                        border: "2px solid #2e7d32",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "38px",
                          fontWeight: 900,
                          color: "#1b5e20",
                          lineHeight: 1,
                        }}
                      >
                        ${minRecord.price.toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        fontSize: "15px",
                        color: "#2e7d32",
                        fontWeight: 600,
                      }}
                    >
                      <Typography>
                        {/* 🏷 */}• Id:{" "}
                        <strong style={{ color: "#1b5e20" }}>
                          {minRecord.priceListId}
                        </strong>
                      </Typography>
                      <Typography>
                        {/* 👥 */}• Supplier:{" "}
                        <strong style={{ color: "#1b5e20" }}>
                          {minRecord.supplier}
                        </strong>
                      </Typography>

                      <Typography>
                        {/* 📌  */}• Grade:{" "}
                        <strong style={{ color: "#1b5e20" }}>
                          {minRecord.grade}
                        </strong>
                      </Typography>

                      <Typography>
                        {/* 📅  */}• Date:{" "}
                        <strong style={{ color: "#1b5e20" }}>
                          {dayjs(minRecord.timestamp).format("MMM DD, YYYY")}
                        </strong>
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            );
          })()}
          <Box mb={2} textAlign="right">
            <Button
              variant="contained"
              onClick={() => setShowChart(!showChart)}
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    transition: "transform 0.3s",
                    transform: showChart ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              }
              sx={{
                background: showChart
                  ? "linear-gradient(45deg, #f44336 30%, #ff7961 90%)"
                  : "linear-gradient(45deg, #4caf50 30%, #81c784 90%)",
                color: "#fff",
                fontWeight: "bold",
                textTransform: "none",
                borderRadius: "50px",
                paddingX: 3,
                paddingY: 1.2,
                // boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
                // transition: "all 0.3s ease",
                // "&:hover": {
                //   boxShadow: "0px 6px 16px rgba(0,0,0,0.3)",
                //   transform: "scale(1.05)",
                // },
              }}
            >
              {showChart ? "Hide Chart" : "Show Chart"}
            </Button>

            <Collapse in={showChart} timeout={400}>
              <Box
                mt={2}
                sx={{
                  mb: 3,
                  borderRadius: 4,
                  overflow: "hidden",
                  border: "1px solid #dcedc8",
                  boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
                  backgroundColor: theme.palette.cardAdd.bgColor,
                }}
              >
                <HighchartsReact highcharts={Highcharts} options={options} />
              </Box>
            </Collapse>
          </Box>
          <Table
            stickyHeader
            size="small"
            // sx={{
            //   mb: 3,
            //   borderRadius: 4,
            //   overflow: "hidden",
            //   border: "1px solid #dcedc8",
            //   boxShadow: "none",
            //   backgroundColor: theme.palette.cardAdd.bgColor,
            // }}
          >
            <TableHead>
              <TableRow>
                <TableCell colSpan={3} />
                <TableCell
                  colSpan={data.length}
                  align="center"
                  sx={{ fontWeight: "bold", background: "#f1f1f1" }}
                >
                  Supplier
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={{ fontWeight: "bold", background: "#fafafa" }}>
                  Price List Id
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", background: "#fafafa" }}>
                  Date
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", background: "#fafafa" }}>
                  Grade
                </TableCell>
                {data.map((series, idx) => (
                  <TableCell
                    key={idx}
                    sx={{ fontWeight: "bold", background: "#fafafa" }}
                    align="right"
                  >
                    {series.name}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {rowsToDisplay.map((row, idx) => {
                const priceValues = data
                  .map((series) => row[series.name])
                  .filter((v) => v !== "-" && !isNaN(v));

                const minPrice = priceValues.length
                  ? Math.min(...priceValues)
                  : "-";

                const foundData = data
                  .flatMap((series) => series.data)
                  .find(([date]) => date === row.timestamp);

                const foundGrade = foundData?.[2] || "";
                const priceListId = foundData?.[3] || "";

                return (
                  <TableRow key={idx} hover>
                    <TableCell>{priceListId}</TableCell>

                    <TableCell>
                      {dayjs(row.timestamp).format("MMM-DD, YYYY")}
                    </TableCell>
                    <TableCell>{foundGrade}</TableCell>

                    {data.map((series, sIdx) => (
                      <TableCell key={sIdx} align="right">
                        {row[series.name] !== "-"
                          ? `$ ${Number(row[series.name]).toFixed(2)}`
                          : "-"}
                      </TableCell>
                    ))}

                    {/* <TableCell
                      align="right"
                      sx={{
                        fontWeight: "bold",
                        background: "#e8f5e9",
                        color: "#1b5e20",
                      }}
                    >
                      {minPrice !== "-"
                        ? `$ ${Number(minPrice).toFixed(2)}`
                        : "-"}
                    </TableCell> */}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {/* Pagination Section */}
          <Grid container justifyContent="flex-end" p={2}>
            <Pagination
              count={tableRows.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
            />
          </Grid>
        </TableContainer>
        {/* <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Date</strong>
                </TableCell>
                <TableCell>
                  <strong>Grade</strong>
                </TableCell>
                {data.map((series, idx) => (
                  <TableCell key={idx}>
                    <strong>{series.name}</strong>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {tableRows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, idx) => {
                  const foundGrade =
                    data
                      .flatMap((series) => series.data)
                      .find(([date]) => date === row.timestamp)?.[2] || "-";

                  return (
                    <TableRow key={idx}>
                      <TableCell>
                        {dayjs(row.timestamp).format("MMM-DD, YYYY")}
                      </TableCell>

                      <TableCell>{foundGrade}</TableCell>

                      {data.map((series, sIdx) => (
                        <TableCell key={sIdx}>
                          {row[series.name] !== "-"
                            ? `$ ${Number(row[series.name]).toFixed(2)}`
                            : "-"}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
          <Grid
            spacing={5}
            p={"18px"}
            py={4}
            justifyContent={"end"}
            container
            textAlign={"center"}
            alignContent={"center"}
            alignItems={"center"}
          >
            <Grid
              sx={{
                textAlign: "end",
                justifyContent: "flex-end",
                alignItems: "flex-end",
              }}
            >
              <Pagination
                count={tableRows.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
              />
            </Grid>
          </Grid>
        </TableContainer> */}
      </Box>
    </Box>
  );
};

export default ProductPriceSplineChart;
